/**
 * Version History screen (#1148, Phase 2).
 * Full-page timeline of request versions, each with a SECTION / FIELD / OLD / NEW change
 * table, per-card Restore, and a long-text detail modal. Built pixel-perfect to Chapman
 * Thompson's approved mockups.
 *
 * @see _bmad-output/planning-artifacts/ux-1148-version-history/ux-1148-version-history.md
 */

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  ActivityIndicator,
  Modal,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import { ConfirmationDialog } from '../components/ConfirmationDialog';
import {
  spokenAuthor,
  withConfigTeamLabel,
} from '../components/correspondence/authorLabel';
import { Header } from '../components/Header';
import { ChevronLeft, ChevronRight, Copy } from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { useRestoreVersion } from '../hooks/mutations/useRestoreVersion';
import { useLocationTags } from '../hooks/queries/useLocationTags';
import { useRequestQuery } from '../hooks/queries/useRequestsQuery';
import {
  useRequestVersionsQuery,
  useRequestVersionDetailQuery,
  useRequestVersionDetailsQueries,
} from '../hooks/queries/useRequestVersionsQuery';
import { useApprovalStampOnlyAccess } from '../hooks/useApprovalStampOnlyAccess';
import { useButtonPermissions } from '../hooks/useButtonPermissions';
import { SummaryRichTextValue } from '../request/steps/summary/SummaryRichTextValue';
import { colors, fontSize, fontWeight, spacing } from '../theme';
import {
  getStageDisplayName,
  normalizeStage,
  RequestStage,
} from '../types/stages';
import {
  copyTextToClipboard,
  EMPTY_DELTA_DISPLAY,
} from '../utils/copyTextToClipboard';
import { dateFormatting } from '../utils/dateFormatting';
import {
  formatDeltaValue,
  formatRichDeltaHtml,
  isLocationField,
  isLongValue,
  looksLikeRichHtml,
  expandDimensionUnitDeltas,
  resolveDeltaDisplay,
  versionSourceLabel,
} from '../utils/versionDeltaDisplay';
import { maskVersionHistoryDeltasForRoles } from '../utils/versionHistoryVisibility';

import type {
  RequestVersionDelta,
  RequestVersionSummary,
} from '../types/RequestVersion';

const isWeb = Platform.OS === 'web';

function unitsFromSnapshot(snapshot: unknown): unknown {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    return undefined;
  }
  if (!('dimensionUnits' in snapshot)) return undefined;
  return (snapshot as { dimensionUnits: unknown }).dimensionUnits;
}

interface VersionHistoryProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

const parseRequestId = (currentPath?: string): number | undefined => {
  const query = (currentPath ?? '').split('?')[1] ?? '';
  const match = /(?:^|&)id=(\d+)/.exec(query);
  return match ? Number(match[1]) : undefined;
};

const formatTimestamp = (iso?: string): string => {
  if (!iso) return '';
  const date = dateFormatting.parseInstant(iso);
  if (!date) return '';
  const time =
    dateFormatting.format(date, {
      hour: 'numeric',
      minute: '2-digit',
    }) ?? '';
  const today = new Date();
  const shown = dateFormatting.calendarDay(date);
  const current = dateFormatting.calendarDay(today);
  const sameDay =
    shown.year === current.year &&
    shown.month === current.month &&
    shown.day === current.day;
  if (sameDay) return `Today, ${time}`;
  const day =
    dateFormatting.format(date, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }) ?? '';
  return `${day}, ${time}`;
};

interface DetailValue {
  fieldName: string;
  oldValue: string;
  newValue: string;
  oldHtml?: string;
  newHtml?: string;
  versionId?: number;
  fieldIndex?: number;
  versionNumber?: number;
  createdAt?: string;
  createdBy?: string | null;
  /** The wire bit (#1599 Story 4.2); only `true` labels the meta line. */
  actorConfigTeam?: boolean;
}

/**
 * The number to print as "Revision N" (#1575 issue 10).
 *
 * ⚠️ NOT `versionNumber`. That is the internal sequence and EVERY row takes one,
 * including the attachment rows this screen no longer shows — so printing it
 * left holes in the numbering ("Revision 10" straight after "Revision 5").
 * `revisionNumber` counts only rows a person is shown. It falls back for a
 * response from before migration 152, and comparisons still use
 * `versionNumber`, which is the row's identity rather than its label.
 */
const revisionLabelOf = (version: {
  versionNumber: number;
  revisionNumber?: number | null;
}): number => version.revisionNumber ?? version.versionNumber;

const valuesFromRaw = (
  oldValue: unknown,
  newValue: unknown,
): Pick<DetailValue, 'oldValue' | 'newValue' | 'oldHtml' | 'newHtml'> => ({
  oldValue: formatDeltaValue(oldValue),
  newValue: formatDeltaValue(newValue),
  oldHtml: looksLikeRichHtml(oldValue) ? String(oldValue) : undefined,
  newHtml: looksLikeRichHtml(newValue) ? String(newValue) : undefined,
});

const detailFromDelta = (
  delta: RequestVersionDelta,
  fieldName: string,
  versionNumber: number,
  createdAt?: string,
  createdBy?: string | null,
  versionId?: number,
  fieldIndex?: number,
  actorConfigTeam?: boolean,
): DetailValue => ({
  fieldName,
  ...valuesFromRaw(delta.oldValue, delta.newValue),
  versionNumber,
  createdAt,
  createdBy,
  versionId,
  fieldIndex,
  actorConfigTeam,
});

const detailsFromDeltas = (
  deltas: RequestVersionDelta[],
  versionNumber: number,
  createdAt?: string,
  createdBy?: string | null,
  versionId?: number,
  actorConfigTeam?: boolean,
): DetailValue[] =>
  deltas.map((delta, fieldIndex) =>
    detailFromDelta(
      delta,
      resolveDeltaDisplay(delta).fieldName,
      versionNumber,
      createdAt,
      createdBy,
      versionId,
      fieldIndex,
      actorConfigTeam,
    ),
  );

const DeltaModalValue: React.FC<{
  preview: string;
  html?: string;
  requestId?: string;
}> = ({ preview, html, requestId }) => {
  if (html && looksLikeRichHtml(html)) {
    const displayHtml = formatRichDeltaHtml(html);
    if (displayHtml.trim()) {
      return <SummaryRichTextValue html={displayHtml} requestId={requestId} />;
    }
  }
  return <Text style={styles.modalValueText}>{preview}</Text>;
};

/** Old-vs-new modal. Prev/next pages every change row across all revisions. */
const ValueDetailModal: React.FC<{
  visible: boolean;
  rows: DetailValue[];
  index: number;
  onIndexChange: (index: number) => void;
  onClose: () => void;
  requestId?: string;
}> = ({ visible, rows, index, onIndexChange, onClose, requestId }) => {
  const [copiedSide, setCopiedSide] = useState<'old' | 'new' | null>(null);
  const copiedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const copyGenerationRef = useRef(0);
  const mountedRef = useRef(true);
  const value = visible ? rows[index] : undefined;
  const oldPreview = value?.oldValue ?? '';
  const newPreview = value?.newValue ?? '';
  const oldHtml = value?.oldHtml;
  const newHtml = value?.newHtml;
  const revisionNumber = value?.versionNumber;
  const createdAt = value?.createdAt;
  // #1599 Story 4.2: "Name · Vertiv Config Team" through the one client composer.
  const createdBy = withConfigTeamLabel(
    value?.createdBy ?? 'System',
    value?.actorConfigTeam,
  );
  const spokenCreatedBy = spokenAuthor(
    value?.createdBy ?? 'System',
    value?.actorConfigTeam,
  );
  const canPrev = visible && index > 0;
  const canNext = visible && index < rows.length - 1;
  const canCopyOld = oldPreview !== '' && oldPreview !== EMPTY_DELTA_DISPLAY;
  const canCopyNew = newPreview !== '' && newPreview !== EMPTY_DELTA_DISPLAY;

  const clearCopiedTimer = useCallback((): void => {
    if (copiedTimerRef.current != null) {
      clearTimeout(copiedTimerRef.current);
      copiedTimerRef.current = null;
    }
  }, []);

  const clearCopiedNotices = useCallback((): void => {
    setCopiedSide(null);
    clearCopiedTimer();
  }, [clearCopiedTimer]);

  useEffect(() => {
    copyGenerationRef.current += 1;
    clearCopiedNotices();
  }, [visible, index, clearCopiedNotices]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      copyGenerationRef.current += 1;
      clearCopiedTimer();
    };
  }, [clearCopiedTimer]);

  const handleCopy = (side: 'old' | 'new', text: string): void => {
    if (!text || text === EMPTY_DELTA_DISPLAY) {
      return;
    }
    const generation = copyGenerationRef.current;
    copyTextToClipboard(text)
      .then(ok => {
        if (
          !ok ||
          !mountedRef.current ||
          generation !== copyGenerationRef.current
        ) {
          return;
        }
        setCopiedSide(side);
        if (copiedTimerRef.current != null) {
          clearTimeout(copiedTimerRef.current);
        }
        copiedTimerRef.current = setTimeout(() => {
          copiedTimerRef.current = null;
          if (mountedRef.current) {
            setCopiedSide(null);
          }
        }, 2500);
      })
      .catch(() => undefined);
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onClose}
    >
      <View style={styles.modalBackdrop}>
        <View style={styles.modalCard}>
          <View style={styles.modalNavRow}>
            <TouchableOpacity
              onPress={() => onIndexChange(index - 1)}
              disabled={!canPrev}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Previous field"
              accessibilityState={{ disabled: !canPrev }}
              testID="revision-history-prev"
              style={styles.modalNavButton}
            >
              <ChevronLeft
                size={22}
                color={canPrev ? colors.textPrimary : colors.textMuted}
                pointerEvents="none"
              />
            </TouchableOpacity>
            <View style={styles.modalNavMeta}>
              <Text style={styles.modalTitle}>{value?.fieldName}</Text>
              {revisionNumber != null ? (
                <Text style={styles.modalRevision}>
                  {`Revision ${revisionNumber}`}
                </Text>
              ) : null}
              <Text
                style={styles.modalMeta}
                accessibilityLabel={
                  spokenCreatedBy +
                  (createdAt ? `, ${formatTimestamp(createdAt)}` : '')
                }
              >
                {createdBy +
                  (createdAt ? ` · ${formatTimestamp(createdAt)}` : '')}
              </Text>
            </View>
            <TouchableOpacity
              onPress={() => onIndexChange(index + 1)}
              disabled={!canNext}
              hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              accessibilityRole="button"
              accessibilityLabel="Next field"
              accessibilityState={{ disabled: !canNext }}
              testID="revision-history-next"
              style={styles.modalNavButton}
            >
              <ChevronRight
                size={22}
                color={canNext ? colors.textPrimary : colors.textMuted}
                pointerEvents="none"
              />
            </TouchableOpacity>
          </View>
          <ScrollView
            style={styles.modalScroll}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
          >
            <View style={styles.modalColumns}>
              <View style={styles.modalColumn}>
                <View style={styles.modalLabelRow}>
                  <Text style={styles.modalOldLabel}>OLD VALUE</Text>
                  <TouchableOpacity
                    onPress={() => handleCopy('old', oldPreview)}
                    disabled={!canCopyOld}
                    style={styles.modalCopyButton}
                    accessibilityRole="button"
                    accessibilityLabel="Copy old value"
                    accessibilityState={{ disabled: !canCopyOld }}
                    testID="revision-history-copy-old"
                  >
                    {copiedSide === 'old' ? (
                      <Text
                        style={styles.modalCopiedHint}
                        testID="revision-history-copied-old"
                        accessibilityLiveRegion="polite"
                        accessibilityRole="text"
                      >
                        Copied
                      </Text>
                    ) : null}
                    <Copy
                      size={16}
                      color={canCopyOld ? colors.textPrimary : colors.textMuted}
                      pointerEvents="none"
                    />
                  </TouchableOpacity>
                </View>
                <DeltaModalValue
                  preview={oldPreview}
                  html={oldHtml}
                  requestId={requestId}
                />
              </View>
              <View style={styles.modalColumn}>
                <View style={styles.modalLabelRow}>
                  <Text style={styles.modalNewLabel}>NEW VALUE</Text>
                  <TouchableOpacity
                    onPress={() => handleCopy('new', newPreview)}
                    disabled={!canCopyNew}
                    style={styles.modalCopyButton}
                    accessibilityRole="button"
                    accessibilityLabel="Copy new value"
                    accessibilityState={{ disabled: !canCopyNew }}
                    testID="revision-history-copy-new"
                  >
                    {copiedSide === 'new' ? (
                      <Text
                        style={styles.modalCopiedHint}
                        testID="revision-history-copied-new"
                        accessibilityLiveRegion="polite"
                        accessibilityRole="text"
                      >
                        Copied
                      </Text>
                    ) : null}
                    <Copy
                      size={16}
                      color={canCopyNew ? colors.textPrimary : colors.textMuted}
                      pointerEvents="none"
                    />
                  </TouchableOpacity>
                </View>
                <DeltaModalValue
                  preview={newPreview}
                  html={newHtml}
                  requestId={requestId}
                />
              </View>
            </View>
          </ScrollView>
          <View style={styles.modalActions}>
            <TouchableOpacity style={styles.modalCloseButton} onPress={onClose}>
              <Text style={styles.modalCloseText}>Close</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );
};

/**
 * OLD/NEW preview. Every changed cell opens the field-revision popup (#1555);
 * underline/truncation stays a long-value hint only.
 */
const ValueCell: React.FC<{
  value: unknown;
  fieldName: string;
  isNew?: boolean;
  onPress: () => void;
}> = ({ value, fieldName, isNew, onPress }) => {
  const text = formatDeltaValue(value);
  const long = isLongValue(text);
  const side = isNew ? 'new' : 'old';
  return (
    <TouchableOpacity
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={`View ${side} value for ${fieldName}`}
      testID={`revision-${side}-value-${fieldName}`}
    >
      <Text
        style={[
          styles.cellValue,
          isNew ? styles.cellValueNew : null,
          long ? styles.cellValueLink : null,
        ]}
        numberOfLines={1}
      >
        {text}
      </Text>
    </TouchableOpacity>
  );
};

const ChangeTable: React.FC<{
  deltas: RequestVersionDelta[];
  onOpenChange: (rows: DetailValue[], index: number) => void;
  versionNumber: number;
  createdAt?: string;
  createdBy?: string | null;
  actorConfigTeam?: boolean;
}> = ({
  deltas,
  onOpenChange,
  versionNumber,
  createdAt,
  createdBy,
  actorConfigTeam,
}) => {
  if (deltas.length === 0) {
    return (
      <Text style={styles.noChanges}>No field changes in this revision.</Text>
    );
  }

  const rows = detailsFromDeltas(
    deltas,
    versionNumber,
    createdAt,
    createdBy,
    undefined,
    actorConfigTeam,
  );
  const openAt = (index: number) => onOpenChange(rows, index);

  if (!isWeb) {
    // Mobile: stack each change into a labeled block (no wide 4-column table).
    return (
      <View style={styles.mobileTable}>
        {deltas.map((delta, index) => {
          const display = resolveDeltaDisplay(delta);
          return (
            <TouchableOpacity
              style={[
                styles.mobileRow,
                index === deltas.length - 1 && styles.rowLast,
              ]}
              key={`${display.sectionName}-${display.fieldName}-${index}`}
              onPress={() => openAt(index)}
              accessibilityRole="button"
              accessibilityLabel={`View change for ${display.fieldName}`}
            >
              <Text style={styles.mobileSection}>{display.sectionName}</Text>
              <Text style={styles.mobileField}>{display.fieldName}</Text>
              <View style={styles.mobileValues}>
                <View style={styles.mobileValueBlock}>
                  <Text style={styles.mobileValueLabel}>OLD</Text>
                  <ValueCell
                    value={delta.oldValue}
                    fieldName={display.fieldName}
                    onPress={() => openAt(index)}
                  />
                </View>
                <View style={styles.mobileValueBlock}>
                  <Text style={styles.mobileValueLabelNew}>NEW</Text>
                  <ValueCell
                    value={delta.newValue}
                    fieldName={display.fieldName}
                    isNew
                    onPress={() => openAt(index)}
                  />
                </View>
              </View>
            </TouchableOpacity>
          );
        })}
      </View>
    );
  }

  return (
    <View style={styles.table}>
      <View style={[styles.row, styles.headerRow]}>
        <Text style={[styles.headerCell, styles.colSection]}>SECTION NAME</Text>
        <Text style={[styles.headerCell, styles.colField]}>FIELD NAME</Text>
        <Text style={[styles.headerCell, styles.colValue]}>OLD VALUE</Text>
        <Text style={[styles.headerCell, styles.colValue]}>NEW VALUE</Text>
      </View>
      {deltas.map((delta, index) => {
        const display = resolveDeltaDisplay(delta);
        return (
          <TouchableOpacity
            style={[
              styles.row,
              styles.changeRow,
              index === deltas.length - 1 && styles.rowLast,
            ]}
            key={`${display.sectionName}-${display.fieldName}-${index}`}
            onPress={() => openAt(index)}
            accessibilityRole="button"
            accessibilityLabel={`View change for ${display.fieldName}`}
          >
            <Text style={[styles.cell, styles.colSection]} numberOfLines={1}>
              {display.sectionName}
            </Text>
            <Text style={[styles.cellField, styles.colField]} numberOfLines={1}>
              {display.fieldName}
            </Text>
            <View style={styles.colValue}>
              <ValueCell
                value={delta.oldValue}
                fieldName={display.fieldName}
                onPress={() => openAt(index)}
              />
            </View>
            <View style={styles.colValue}>
              <ValueCell
                value={delta.newValue}
                fieldName={display.fieldName}
                isNew
                onPress={() => openAt(index)}
              />
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const VersionCard: React.FC<{
  version: RequestVersionSummary;
  requestId?: number;
  isCurrent: boolean;
  onOpenChange: (rows: DetailValue[], index: number) => void;
  onRestore: (version: RequestVersionSummary) => void;
  canRestore: boolean;
  decorateDeltas: (
    deltas: RequestVersionDelta[],
    savedUnits?: unknown,
  ) => RequestVersionDelta[];
}> = ({
  version,
  requestId,
  isCurrent,
  onOpenChange,
  onRestore,
  canRestore,
  decorateDeltas,
}) => {
  const showRestore = !isCurrent && version.isRestoreCompatible && canRestore;
  const hasMore = version.deltaCount > version.deltaPreview.length;
  // "+ N more change(s)" is expandable (#1148, Kiran #18). The full delta is only fetched once
  // the user expands — a disabled query (versionId undefined) means collapsed cards cost nothing.
  const [expanded, setExpanded] = useState(false);
  const detailQuery = useRequestVersionDetailQuery(
    requestId,
    expanded ? version.id : undefined,
  );
  const fullDelta = detailQuery.data?.delta ?? undefined;
  const shownDeltas =
    expanded && fullDelta
      ? decorateDeltas(
          fullDelta,
          unitsFromSnapshot(detailQuery.data?.snapshot) ??
            version.dimensionUnits,
        )
      : version.deltaPreview;
  return (
    <View style={styles.timelineRow}>
      <View style={styles.timelineRail}>
        <View
          style={[styles.timelineDot, isCurrent && styles.timelineDotCurrent]}
        >
          {isCurrent ? <Text style={styles.timelineCheck}>✓</Text> : null}
        </View>
        <View style={styles.timelineLine} />
      </View>
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <View style={styles.cardHeaderTitleRow}>
            <Text style={styles.cardTitle}>
              {`Revision ${revisionLabelOf(version)}`}
            </Text>
            <Text style={styles.cardSource}>
              {versionSourceLabel(version.source)}
            </Text>
            {isCurrent ? (
              <View style={styles.currentPill}>
                <Text style={styles.currentPillText}>Latest</Text>
              </View>
            ) : null}
          </View>
          {showRestore ? (
            <TouchableOpacity
              style={styles.restoreButton}
              onPress={() => onRestore(version)}
            >
              <Text style={styles.restoreButtonText}>↺ Restore</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        <Text
          style={styles.cardMeta}
          // #1599 Story 4.2: the spoken form, as the discussion speaks it.
          accessibilityLabel={`${spokenAuthor(
            version.createdBy ?? 'System',
            version.actorConfigTeam,
          )}, ${formatTimestamp(version.createdAt)}`}
        >
          {withConfigTeamLabel(
            version.createdBy ?? 'System',
            version.actorConfigTeam,
          ) +
            ' · ' +
            formatTimestamp(version.createdAt)}
        </Text>
        <ChangeTable
          deltas={shownDeltas}
          onOpenChange={onOpenChange}
          versionNumber={revisionLabelOf(version)}
          createdAt={version.createdAt}
          createdBy={version.createdBy}
          actorConfigTeam={version.actorConfigTeam}
        />
        {hasMore ? (
          expanded && detailQuery.isLoading ? (
            <Text style={styles.moreChanges}>Loading all changes…</Text>
          ) : expanded && detailQuery.isError ? (
            <TouchableOpacity
              onPress={() => detailQuery.refetch()}
              accessibilityRole="button"
              accessibilityLabel="Retry loading all changes"
            >
              <Text style={styles.moreChangesLink}>
                Couldn’t load changes — tap to retry
              </Text>
            </TouchableOpacity>
          ) : expanded && fullDelta ? (
            <TouchableOpacity
              onPress={() => setExpanded(false)}
              accessibilityRole="button"
              accessibilityLabel="Show fewer changes"
            >
              <Text style={styles.moreChangesLink}>Show fewer</Text>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => setExpanded(true)}
              accessibilityRole="button"
              accessibilityLabel={`Show all ${version.deltaCount} changes`}
            >
              <Text style={styles.moreChangesLink}>
                + {version.deltaCount - version.deltaPreview.length} more
                change(s)
              </Text>
            </TouchableOpacity>
          )
        ) : null}
      </View>
    </View>
  );
};

/** The app's canonical, user-facing stage label (e.g. SITE_ASSESSMENT -> "In Progress"). */
const stageLabel = (stage?: string): string =>
  stage ? getStageDisplayName(stage as RequestStage) : 'its current stage';

export const VersionHistory: React.FC<VersionHistoryProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const requestId = useMemo(() => parseRequestId(currentPath), [currentPath]);
  const { data, isLoading, isError } = useRequestVersionsQuery(requestId, {
    pageSize: 50,
  });
  const restore = useRestoreVersion(requestId ?? 0);
  const { data: request } = useRequestQuery(
    requestId !== undefined ? String(requestId) : '',
  );
  const requestStage = (request as { sysRequestStage?: string } | undefined)
    ?.sysRequestStage;
  const { canShowRestore } = useButtonPermissions(
    requestStage,
    requestId !== undefined ? String(requestId) : undefined,
  );
  const requestorEmail = (request as { requestorEmail?: string } | undefined)
    ?.requestorEmail;
  const normalizedStage = requestStage ? normalizeStage(requestStage) : null;
  const { approverMutationsBlocked } = useApprovalStampOnlyAccess({
    requestorEmail,
    requestStage: normalizedStage,
  });
  const canRestoreVersion = canShowRestore && !approverMutationsBlocked;

  const [openChange, setOpenChange] = useState<{
    versionId: number;
    fieldIndex: number;
  } | null>(null);
  const [pendingRestore, setPendingRestore] =
    useState<RequestVersionSummary | null>(null);

  const versions = data?.versions ?? [];
  const currentVersionNumber = versions[0]?.versionNumber ?? 0;

  // Unit Location (#1148 Epic 5, Kiran #5): a location assignment is captured as a
  // `locationTagId` child-field change; swap the raw tag id for the tag name users know.
  const locationTags = useLocationTags(
    requestId !== undefined ? String(requestId) : '',
    requestId !== undefined,
  );
  const tagNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of locationTags.data ?? []) {
      if (t.id != null) map.set(t.id, t.name);
    }
    return map;
  }, [locationTags.data]);
  // Location-tag decoration (Kiran #5) extracted so it can decorate BOTH the compact preview and
  // the expanded full delta (#18) — id → tag-name resolution stays consistent when the user
  // expands "+ N more change(s)".
  const decorateDeltas = useCallback(
    (
      deltas: RequestVersionDelta[],
      savedUnits?: unknown,
    ): RequestVersionDelta[] => {
      const resolveTag = (value: unknown): unknown =>
        value == null ? value : tagNameById.get(Number(value)) ?? value;
      // Kiran #24 (#1501): on a unit add/remove the `locationTagId` is NESTED
      // inside the row-summary object (not a top-level field), so the id →
      // tag-name swap must reach into it — otherwise the added/removed row
      // shows "Location: 17" instead of the tag name.
      const resolveNested = (value: unknown): unknown => {
        if (!value || typeof value !== 'object' || Array.isArray(value)) {
          return value;
        }
        const obj = value as Record<string, unknown>;
        if (!('locationTagId' in obj)) return value;
        return { ...obj, locationTagId: resolveTag(obj.locationTagId) };
      };
      return expandDimensionUnitDeltas(
        maskVersionHistoryDeltasForRoles(deltas, user?.roles ?? []).map(d => {
          if (isLocationField(d.field)) {
            return {
              ...d,
              oldValue: resolveTag(d.oldValue),
              newValue: resolveTag(d.newValue),
            };
          }
          if (
            d.scope === 'child' &&
            (d.action === 'create' || d.action === 'delete')
          ) {
            return {
              ...d,
              oldValue: resolveNested(d.oldValue),
              newValue: resolveNested(d.newValue),
            };
          }
          return d;
        }),
        savedUnits,
      );
    },
    [tagNameById, user?.roles],
  );
  const decoratedVersions = useMemo(
    () =>
      versions.map(v => ({
        ...v,
        deltaPreview: decorateDeltas(v.deltaPreview, v.dimensionUnits),
      })),
    [versions, decorateDeltas],
  );

  // Cross-stage restore (#1148 Epic 5, Kiran #3): restore never changes the request's stage
  // (the backend omits stage from the restorable patch) — only field values. If the selected
  // version was captured in a different stage than the live request, warn the user explicitly.
  const pendingDetail = useRequestVersionDetailQuery(
    requestId,
    pendingRestore?.id,
  );
  // Popup pager is oldest-first so left moves to lower revision numbers and right moves to
  // higher ones (#1557 QA). The visible timeline remains newest-first.
  const versionIds = useMemo(
    () => decoratedVersions.map(version => version.id),
    [decoratedVersions],
  );
  const versionDetails = useRequestVersionDetailsQueries(
    requestId,
    versionIds,
    openChange !== null,
  );
  const modalRows = useMemo(() => {
    if (!openChange) return [];
    const deltaByVersionId = new Map<number, RequestVersionDelta[]>();
    const unitsByVersionId = new Map<number, unknown>();
    versionDetails.forEach((result, index) => {
      const versionId = versionIds[index];
      if (versionId == null) return;
      const full = result.data?.delta;
      if (Array.isArray(full) && full.length > 0) {
        deltaByVersionId.set(versionId, full);
      }
      const units = unitsFromSnapshot(result.data?.snapshot);
      if (units !== undefined) unitsByVersionId.set(versionId, units);
    });
    return [...decoratedVersions].reverse().flatMap(version => {
      if (version.deltaCount === 0 && version.deltaPreview.length === 0) {
        return [];
      }
      const full = deltaByVersionId.get(version.id);
      const deltas =
        Array.isArray(full) && full.length > 0
          ? decorateDeltas(
              full,
              unitsByVersionId.get(version.id) ?? version.dimensionUnits,
            )
          : version.deltaPreview;
      return detailsFromDeltas(
        deltas,
        version.versionNumber,
        version.createdAt,
        version.createdBy,
        version.id,
        version.actorConfigTeam,
      );
    });
  }, [
    openChange,
    decoratedVersions,
    decorateDeltas,
    versionDetails,
    versionIds,
  ]);
  const modalIndex = useMemo(() => {
    if (!openChange) return 0;
    const found = modalRows.findIndex(
      row =>
        row.versionId === openChange.versionId &&
        row.fieldIndex === openChange.fieldIndex,
    );
    return found >= 0 ? found : 0;
  }, [openChange, modalRows]);
  const pendingSnapshot = pendingDetail.data?.snapshot as
    | { sysRequestStage?: string; stage?: string }
    | undefined;
  const pendingStage =
    pendingSnapshot?.sysRequestStage ?? pendingSnapshot?.stage;
  const isCrossStage =
    !!pendingStage && !!requestStage && pendingStage !== requestStage;
  const restoreMessage = isCrossStage
    ? `This restores the field values from Revision ${
        pendingRestore ? revisionLabelOf(pendingRestore) : ''
      } and records a new revision. The request stays in its current stage (${stageLabel(
        requestStage,
      )}) — only values change; the stage is not reverted. This cannot be undone except by restoring again.`
    : 'This replaces the current request content with the selected revision and records it as a new revision. This cannot be undone except by restoring again.';

  const backToRequest = () =>
    navigate(requestId ? `/request-detail?id=${requestId}` : '/request');

  return (
    <View style={styles.container}>
      <Header
        currentPath={currentPath || '/request-versions'}
        navigate={navigate}
      />

      <View style={styles.subHeader}>
        <TouchableOpacity style={styles.backLink} onPress={backToRequest}>
          <Text style={styles.backLinkText}>‹ Back to Request</Text>
        </TouchableOpacity>
        <Text style={styles.subHeaderLabel}>Revision History</Text>
      </View>

      <ScrollView
        style={styles.scroll}
        contentContainerStyle={styles.content}
        nestedScrollEnabled
        keyboardShouldPersistTaps="handled"
        testID="revision-history-list"
      >
        <View style={styles.column}>
          <Text style={styles.pageTitle}>Revision History</Text>

          {isLoading ? (
            <View style={styles.stateBox}>
              <ActivityIndicator color={colors.primary} />
              <Text style={styles.stateText}>Loading revision history…</Text>
            </View>
          ) : isError ? (
            <View style={styles.stateBox}>
              <Text style={styles.stateText}>
                Could not load revision history.
              </Text>
            </View>
          ) : versions.length === 0 ? (
            <View style={styles.stateBox}>
              <Text style={styles.stateText}>
                No revision history yet for this request.
              </Text>
            </View>
          ) : (
            decoratedVersions.map((version, index) => (
              <VersionCard
                key={version.id}
                version={version}
                requestId={requestId}
                isCurrent={index === 0}
                onOpenChange={(_rows, rowIndex) =>
                  setOpenChange({
                    versionId: version.id,
                    fieldIndex: rowIndex,
                  })
                }
                onRestore={setPendingRestore}
                canRestore={canRestoreVersion}
                decorateDeltas={decorateDeltas}
              />
            ))
          )}
        </View>
      </ScrollView>

      <ValueDetailModal
        visible={openChange !== null}
        rows={modalRows}
        index={modalIndex}
        onIndexChange={nextIndex => {
          const nextRow = modalRows[nextIndex];
          if (
            !nextRow ||
            nextRow.versionId == null ||
            nextRow.fieldIndex == null
          ) {
            return;
          }
          setOpenChange({
            versionId: nextRow.versionId,
            fieldIndex: nextRow.fieldIndex,
          });
        }}
        onClose={() => setOpenChange(null)}
        requestId={requestId !== undefined ? String(requestId) : undefined}
      />

      <ConfirmationDialog
        visible={pendingRestore !== null}
        onClose={() => setPendingRestore(null)}
        onConfirm={async () => {
          if (!pendingRestore || requestId === undefined) return;
          await restore.mutateAsync({
            versionId: pendingRestore.id,
            body: {
              mode: 'full',
              expectedCurrentVersionNumber: currentVersionNumber,
            },
          });
          setPendingRestore(null);
        }}
        title={
          pendingRestore
            ? `Restore Revision ${revisionLabelOf(pendingRestore)}?`
            : 'Restore revision?'
        }
        message={restoreMessage}
        confirmText="Restore"
        loadingText="Restoring…"
        confirmButtonStyle="danger"
      />
    </View>
  );
};

export default VersionHistory;

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: 0,
    backgroundColor: colors.surfaceRequestCanvas,
    ...Platform.select({
      web: { overflow: 'hidden' as const, height: '100%' },
    }),
  },
  subHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.backgroundSecondary,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderCard,
    paddingHorizontal: spacing.screenPaddingHorizontal,
    paddingVertical: spacing.md,
    gap: spacing.lg,
  },
  backLink: { flexDirection: 'row', alignItems: 'center' },
  backLinkText: { color: colors.textSecondary, fontSize: fontSize.formLabel },
  subHeaderLabel: {
    color: colors.primary,
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.semibold,
  },
  scroll: {
    flex: 1,
    minHeight: 0,
    ...Platform.select({
      web: { overflowY: 'auto' as const },
    }),
  },
  content: {
    // Stretch so the timeline can grow past the viewport; the column centers itself.
    alignItems: 'stretch',
    flexGrow: 1,
    paddingHorizontal: spacing.screenPaddingHorizontal,
    paddingVertical: spacing.xl,
  },
  column: {
    width: '100%',
    maxWidth: isWeb ? 880 : undefined,
    alignSelf: isWeb ? 'center' : 'stretch',
  },
  pageTitle: {
    fontSize: fontSize.pageTitle,
    fontWeight: fontWeight.bold,
    color: colors.textPrimary,
    marginBottom: spacing.xl,
  },
  stateBox: {
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderCard,
    padding: spacing.xl,
    alignItems: 'center',
    gap: spacing.sm,
  },
  stateText: { color: colors.textSecondary, fontSize: fontSize.body },

  // Timeline + card
  timelineRow: { flexDirection: 'row' },
  timelineRail: { width: 28, alignItems: 'center' },
  timelineDot: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: colors.borderMedium,
    backgroundColor: colors.backgroundPrimary,
    marginTop: spacing.lg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  timelineDotCurrent: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  timelineCheck: { color: colors.textLight, fontSize: 10, fontWeight: '700' },
  timelineLine: {
    flex: 1,
    width: 2,
    backgroundColor: colors.borderCard,
    marginTop: spacing.xs,
  },
  card: {
    flex: 1,
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.cardBorderRadius,
    borderWidth: 1,
    borderColor: colors.borderCard,
    padding: spacing.lg,
    marginBottom: spacing.xl,
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  cardHeaderTitleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flexShrink: 1,
  },
  cardTitle: {
    fontSize: fontSize.subheading,
    fontWeight: fontWeight.bold,
    color: colors.textPrimary,
  },
  cardSource: { fontSize: fontSize.cardSubtitle, color: colors.textMuted },
  currentPill: {
    backgroundColor: '#FFEDE5',
    borderRadius: 10,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
  },
  currentPillText: {
    color: colors.primaryDark,
    fontSize: 10,
    fontWeight: fontWeight.bold,
    letterSpacing: 0.5,
  },
  restoreButton: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.xs,
  },
  restoreButtonText: {
    color: colors.textPrimary,
    fontSize: fontSize.cardSubtitle,
    fontWeight: fontWeight.medium,
  },
  cardMeta: {
    fontSize: fontSize.cardSubtitle,
    color: colors.textMuted,
    marginTop: spacing.xs,
    marginBottom: spacing.md,
  },

  // Change table
  table: {
    borderWidth: 1,
    borderColor: colors.borderHairline,
    borderRadius: 6,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    borderBottomWidth: 1,
    borderBottomColor: colors.borderHairline,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
  },
  rowLast: { borderBottomWidth: 0 },
  changeRow: {
    ...Platform.select({
      web: { cursor: 'pointer' as const },
    }),
  },
  headerRow: { backgroundColor: colors.surfaceTableHeader },
  headerCell: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    color: colors.textMuted,
    letterSpacing: 0.5,
  },
  colSection: { flexBasis: '22%', flexGrow: 0, paddingRight: spacing.sm },
  colField: { flexBasis: '30%', flexGrow: 0, paddingRight: spacing.sm },
  colValue: { flexBasis: '24%', flexGrow: 1, paddingRight: spacing.sm },
  cell: { fontSize: fontSize.cardSubtitle, color: colors.textSecondary },
  cellField: {
    fontSize: fontSize.cardSubtitle,
    color: colors.textPrimary,
    fontWeight: fontWeight.medium,
  },
  cellValue: { fontSize: fontSize.cardSubtitle, color: colors.textSecondary },
  cellValueNew: { color: colors.textPrimary },
  cellValueLink: { textDecorationLine: 'underline' },
  noChanges: {
    fontSize: fontSize.cardSubtitle,
    color: colors.textMuted,
    fontStyle: 'italic',
  },
  moreChanges: {
    fontSize: fontSize.charCount,
    color: colors.textMuted,
    marginTop: spacing.sm,
  },
  moreChangesLink: {
    fontSize: fontSize.charCount,
    color: colors.primary,
    fontWeight: fontWeight.medium,
    marginTop: spacing.sm,
    paddingVertical: spacing.xs,
  },

  // Change table — mobile (stacked, native)
  mobileTable: {
    borderWidth: 1,
    borderColor: colors.borderHairline,
    borderRadius: 6,
    overflow: 'hidden',
  },
  mobileRow: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderHairline,
    padding: spacing.md,
    gap: spacing.xs,
  },
  mobileSection: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    color: colors.textMuted,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  },
  mobileField: {
    fontSize: fontSize.body,
    fontWeight: fontWeight.medium,
    color: colors.textPrimary,
  },
  mobileValues: {
    flexDirection: 'row',
    gap: spacing.lg,
    marginTop: spacing.xs,
  },
  mobileValueBlock: { flex: 1, gap: 2 },
  mobileValueLabel: {
    fontSize: 10,
    fontWeight: fontWeight.semibold,
    color: colors.textMuted,
    letterSpacing: 0.5,
  },
  mobileValueLabelNew: {
    fontSize: 10,
    fontWeight: fontWeight.semibold,
    color: colors.success,
    letterSpacing: 0.5,
  },

  // Detail modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: colors.overlayBackdrop,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.xl,
  },
  modalCard: {
    width: '100%',
    maxWidth: 640,
    // Bound the height so long values scroll instead of pushing Close off-screen (mobile).
    maxHeight: '85%',
    minHeight: 0,
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    borderRadius: spacing.cardBorderRadius,
    padding: spacing.modalPadding,
  },
  modalTitle: {
    fontSize: fontSize.sectionTitle,
    fontWeight: fontWeight.bold,
    color: colors.textPrimary,
  },
  modalNavRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.lg,
  },
  modalNavButton: {
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs,
  },
  modalNavMeta: { flex: 1, gap: 2 },
  modalRevision: {
    fontSize: fontSize.body,
    fontWeight: fontWeight.semibold,
    color: colors.textPrimary,
  },
  modalMeta: {
    fontSize: fontSize.cardSubtitle,
    color: colors.textMuted,
  },
  modalLabelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  modalCopyButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  modalCopiedHint: {
    fontSize: fontSize.charCount,
    // Match Field Text Assist's established neutral copy confirmation (#1548 QA).
    color: colors.textSecondary,
    fontWeight: fontWeight.medium,
  },
  // Lets long values scroll within the height-bounded card instead of clipping.
  modalScroll: {
    flexGrow: 1,
    flexShrink: 1,
    minHeight: 0,
    ...Platform.select({
      web: { overflowY: 'auto' as const },
    }),
  },
  modalColumns: { flexDirection: isWeb ? 'row' : 'column', gap: spacing.xl },
  // On web the columns share the row (flex: 1). On native they stack in an auto-height card,
  // where `flex: 1` collapses the value text to zero height (Kiran #10) — size to content.
  modalColumn: { flex: isWeb ? 1 : 0, gap: spacing.xs },
  modalOldLabel: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    color: colors.textMuted,
    letterSpacing: 0.5,
  },
  modalNewLabel: {
    fontSize: 11,
    fontWeight: fontWeight.semibold,
    color: colors.success,
    letterSpacing: 0.5,
  },
  modalValueText: {
    fontSize: fontSize.body,
    color: colors.textPrimary,
    lineHeight: 20,
  },
  modalActions: { alignItems: 'flex-end', marginTop: spacing.xl },
  modalCloseButton: {
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: spacing.formControlBorderRadius,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  modalCloseText: {
    color: colors.textPrimary,
    fontSize: fontSize.button,
    fontWeight: fontWeight.medium,
  },
});
